/**
 * /mi-cuenta/exportar — Vista previa imprimible del reporte de salud del usuario.
 *
 * Render:
 *  • Reporte ejecutivo HTML/CSS estilizado a ancho A4 (794px @ 96dpi).
 *  • Cada sección marcada con `data-pdf-slide` se captura como página independiente
 *    (html2canvas → jsPDF), evitando cortes a media sección.
 *
 * Datos:
 *  • profile (UserProfileContext)
 *  • mediciones de seguimiento (subcolección users/{uid}/measurements)
 *  • profile.recommendations.data: { summary, components } (cache IA en Firestore)
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, CheckCircle, XCircle, AlertTriangle, Activity, Heart, Apple, Brain, Dumbbell, FileText, Sparkles, Target, Flame, BookOpen, Sun, Moon, Lightbulb } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { listMeasurements } from "@/services/measurements";
import {
  calculateIMC, classifyIMC, classifyBP, classifyGlucose, calculateAge,
} from "@/pages/FormF1/helpers";
import {
  ipaqLabel, dietCatLabel, behaviorCatLabel, hydrationLabel,
  emotionalLabel, anxietyLabel, stressLabel, wellbeingLabel, sedLabel,
} from "@/pages/Dashboard/helpers";

// Brand colors (WE360)
const TEAL = "#00628F";
const MAGENTA = "#883476";
const YELLOW = "#F9A01B";
const GREEN = "#157346";
const RED = "#C0453A";
const TEAL_LIGHT = "#E0F2FE";
const MAGENTA_LIGHT = "#FCE7F3";
const SLATE = "#1E293B";
const SLATE_400 = "#94A3B8";
const SLATE_500 = "#64748B";

// A4 dimensions at 96dpi: 794 x 1123 px (portrait)
const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;

// ── helpers ────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-PA", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function statusFromColor(color) {
  if (color === GREEN) return "OK";
  if (color === YELLOW) return "Atención";
  if (color === RED) return "Riesgo";
  return "—";
}

// ── small components ───────────────────────────────────────────────────────

function VitalCard({ icon: Icon, label, value, unit, status, color = SLATE }) {
  return (
    <div
      style={{
        background: "white",
        borderLeft: `4px solid ${color}`,
        padding: "12px 14px",
        borderRadius: 10,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 32, height: 32, borderRadius: 8,
          background: `${color}1a`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={16} style={{ color }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 9, color: SLATE_400, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, margin: 0 }}>
          {label}
        </p>
        <p style={{ fontSize: 16, color: SLATE, fontWeight: 700, margin: 0, lineHeight: 1.2 }}>
          {value || "—"}
          {value && unit && <span style={{ fontSize: 10, color: SLATE_400, fontWeight: 500, marginLeft: 4 }}>{unit}</span>}
        </p>
      </div>
      {status && (
        <span style={{
          fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
          background: `${color}1a`, color, whiteSpace: "nowrap",
        }}>
          {status}
        </span>
      )}
    </div>
  );
}

function FormCheck({ label, done, score }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 12px", borderRadius: 8,
        background: done ? "#F0FDF4" : "#F8FAFC",
        border: `1px solid ${done ? "#BBF7D0" : "#E2E8F0"}`,
      }}
    >
      {done
        ? <CheckCircle size={14} style={{ color: GREEN, flexShrink: 0 }} />
        : <XCircle size={14} style={{ color: SLATE_400, flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: done ? "#166534" : SLATE_400, margin: 0 }}>{label}</p>
        {score && (
          <p style={{ fontSize: 9, color: done ? "#16A34A" : SLATE_400, margin: 0 }}>{score}</p>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children, accent = TEAL, style = {} }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, ...style }}>
      <div style={{ width: 4, height: 18, background: accent, borderRadius: 2 }} />
      <h2 style={{ fontSize: 13, fontWeight: 800, color: SLATE, margin: 0, textTransform: "uppercase", letterSpacing: 0.6 }}>
        {children}
      </h2>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div>
      <p style={{ fontSize: 9, color: SLATE_400, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, margin: 0, marginBottom: 2 }}>
        {label}
      </p>
      <p style={{ fontSize: 12, color: SLATE, fontWeight: 500, margin: 0, lineHeight: 1.35 }}>
        {value || "—"}
      </p>
    </div>
  );
}

// ── PDF page wrapper ───────────────────────────────────────────────────────

function PdfPage({ children, padded = true, gradient = false }) {
  return (
    <div
      data-pdf-slide="true"
      style={{
        width: PAGE_WIDTH,
        minHeight: PAGE_HEIGHT,
        background: gradient ? `linear-gradient(135deg, ${TEAL} 0%, ${MAGENTA} 100%)` : "white",
        padding: padded ? "32px 40px" : 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        marginBottom: 24, // gap on screen between slides; ignored in PDF
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.08)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

function PageFooter({ pageNum, totalPages, patient }) {
  return (
    <div style={{
      marginTop: "auto", paddingTop: 14, borderTop: "1px solid #E2E8F0",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
    }}>
      <p style={{ fontSize: 9, color: SLATE_400, margin: 0 }}>
        WE360 · Reporte preventivo de salud · {patient}
      </p>
      <p style={{ fontSize: 9, color: SLATE_400, margin: 0 }}>
        Página {pageNum} / {totalPages}
      </p>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────

export default function ExportPreview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading } = useUserProfile();

  const containerRef = useRef(null);
  const [measurements, setMeasurements] = useState([]);
  const [loadingMeas, setLoadingMeas] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const [scale, setScale] = useState(1);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    if (!user?.uid) return;
    listMeasurements(user.uid, 30)
      .then((rows) => setMeasurements(rows.reverse())) // older→newer for chart
      .catch(() => setMeasurements([]))
      .finally(() => setLoadingMeas(false));
  }, [user?.uid]);

  useEffect(() => {
    if (!containerRef.current) return;

    let frameId = null;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const height = entry.target.offsetHeight;
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          setContainerHeight((prev) => (Math.abs(prev - height) > 2 ? height : prev));
        });
      }
    });
    observer.observe(containerRef.current);

    function handleResize() {
      const screenWidth = window.innerWidth;
      const targetWidth = PAGE_WIDTH + 32;
      if (screenWidth < targetWidth) {
        setScale((screenWidth - 32) / PAGE_WIDTH);
      } else {
        setScale(1);
      }
    }

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (loading || !profile) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F1F5F9" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 40, height: 40, border: "4px solid #00628F", borderTopColor: "transparent",
            borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px"
          }}></div>
          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
          <p style={{ fontSize: 14, fontWeight: 500, color: "#475569" }}>Cargando reporte de salud...</p>
        </div>
      </div>
    );
  }

  // Derived values
  const fullName = [profile.nombres, profile.apellidos].filter(Boolean).join(" ") || "—";
  const age = calculateAge(profile.fechaNacimiento);
  const imc = calculateIMC(profile.peso, profile.talla);
  const imcCls = classifyIMC(imc);
  const bpCls = classifyBP(profile.paSistolica, profile.paDiastolica);
  const gluCls = classifyGlucose(profile.glucemia);

  const oxiVal = parseFloat(profile.oximetria);
  const oxiColor = !isNaN(oxiVal) ? (oxiVal >= 95 ? GREEN : oxiVal >= 90 ? YELLOW : RED) : SLATE_400;

  const hasF1 = !!profile.completedAt;
  const hasF2 = !!profile.f2Results?.completedAt;
  const hasF3 = !!profile.f3Results?.completedAt;
  const hasF4 = !!profile.f4Results?.completedAt;
  const hasF5 = !!profile.f5Results?.completedAt;
  const completed = [hasF1, hasF2, hasF3, hasF4, hasF5].filter(Boolean).length;

  // Build chart data (last up to 12 measurements)
  const chartFields = ["paSistolica", "paDiastolica", "glucemia", "heartRate", "oximetria"];
  const chartRows = measurements
    .filter((m) => chartFields.some((k) => m.fields?.[k] != null))
    .slice(-12)
    .map((m) => ({
      date: fmtDate(m.createdAt).replace(/\sde\s/g, " "),
      Sistólica: m.fields?.paSistolica ?? null,
      Diastólica: m.fields?.paDiastolica ?? null,
      Glucemia: m.fields?.glucemia ?? null,
      "FC": m.fields?.heartRate ?? null,
      "SpO₂": m.fields?.oximetria ?? null,
    }));

  // Split into 2 charts: blood pressure vs others
  const bpRows = chartRows.filter((r) => r.Sistólica != null || r.Diastólica != null);
  const otherRows = chartRows.filter((r) => r.Glucemia != null || r["FC"] != null || r["SpO₂"] != null);

  const bpSeries = [
    { key: "Sistólica", color: TEAL },
    { key: "Diastólica", color: "#0EA5E9" },
  ].filter((s) => bpRows.some((row) => row[s.key] != null));

  const otherSeries = [
    { key: "Glucemia", color: MAGENTA },
    { key: "FC", color: YELLOW },
    { key: "SpO₂", color: GREEN },
  ].filter((s) => otherRows.some((row) => row[s.key] != null));

  const hasAnyChart = bpSeries.length > 0 || otherSeries.length > 0;

  // AI recommendations (real structure from /recommendations page)
  const ai = profile.recommendations?.data || null;
  const summary = ai?.summary || null;
  const components = ai?.components || null;
  const physical = components?.physical || null;
  const nutrition = components?.nutrition || null;
  const emotional = components?.emotional || null;
  const capsulaNeuro = emotional?.educacion_neurociencia?.capsula_microlearning || null;
  const hasAI = !!(summary?.resumen_ejecutivo || summary?.plan_maestro_hoy?.prioridad_numero_1 || physical || nutrition || emotional);

  // Determine number of slides for footer numbering
  // Slide 1: Hero + identity + datos + signos vitales + condiciones/medicamentos
  // Slide 2: Historial (gráficos) + Checklist F1-F5
  // Slide 3: Detalle clínico por dimensión (sólo si tiene F2-F5)
  // Slide 4: AI summary (resumen + alertas + plan + motivación)
  // Slide 5: Pilar Físico
  // Slide 6: Pilar Nutricional
  // Slide 7: Pilar Emocional
  // Slide 8: Cápsula de neurociencia (sólo si capsula es larga)
  const showDetailSlide = hasF2 || hasF3 || hasF4 || hasF5;
  const showPhysical = !!physical;
  const showNutrition = !!nutrition;
  const showEmotional = !!emotional;
  const showCapsulaSlide = !!(capsulaNeuro && capsulaNeuro.length > 350);
  const totalPages = 2
    + (showDetailSlide ? 1 : 0)
    + (hasAI ? 1 : 0)
    + (showPhysical ? 1 : 0)
    + (showNutrition ? 1 : 0)
    + (showEmotional ? 1 : 0)
    + (showCapsulaSlide ? 1 : 0);

  const pageNumbers = (() => {
    let n = 1;
    const map = {};
    map.cover = n++;
    map.history = n++;
    if (showDetailSlide) map.detail = n++;
    if (hasAI) map.aiSummary = n++;
    if (showPhysical) map.physical = n++;
    if (showNutrition) map.nutrition = n++;
    if (showEmotional) map.emotional = n++;
    if (showCapsulaSlide) map.aiCapsule = n++;
    return map;
  })();

  async function handleDownload() {
    if (!containerRef.current) return;
    setDownloading(true);
    const container = containerRef.current;
    const originalTransform = container.style.transform;
    try {
      // Temporarily disable scale transform to prevent html2canvas rendering/overlapping bugs on mobile
      container.style.transform = "none";

      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const slides = container.querySelectorAll('[data-pdf-slide="true"]');
      if (slides.length === 0) throw new Error("No slides found");

      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        const canvas = await html2canvas(slide, {
          scale: 1.5,
          backgroundColor: "#FFFFFF",
          useCORS: true,
          logging: false,
          windowWidth: PAGE_WIDTH,
          width: PAGE_WIDTH,
          height: slide.offsetHeight,
        });

        // If a slide is taller than 1 A4 page, slice the canvas vertically
        // into multiple A4-fitting chunks. Otherwise, single page.
        const slideHeightPx = slide.offsetHeight;
        const overflows = slideHeightPx > PAGE_HEIGHT + 8;

        if (!overflows) {
          // Single page: fit canvas into A4
          const imgData = canvas.toDataURL("image/jpeg", 0.92);
          const imgRatio = canvas.height / canvas.width;
          const imgW = pdfW;
          const imgH = imgW * imgRatio;
          const finalH = Math.min(imgH, pdfH);
          const finalW = finalH === pdfH ? pdfH / imgRatio : imgW;
          const offsetX = (pdfW - finalW) / 2;
          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", offsetX, 0, finalW, finalH);
        } else {
          // Multi-page slice. Each chunk is PAGE_HEIGHT in CSS pixels
          // (=> canvas pixels = PAGE_HEIGHT * scale).
          const scaleVal = canvas.width / PAGE_WIDTH;
          const chunkPxCss = PAGE_HEIGHT;
          const chunkPxCanvas = chunkPxCss * scaleVal;
          let yOffsetCanvas = 0;
          let isFirstChunk = true;
          while (yOffsetCanvas < canvas.height) {
            const remaining = canvas.height - yOffsetCanvas;
            const sliceH = Math.min(chunkPxCanvas, remaining);
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = canvas.width;
            tempCanvas.height = sliceH;
            const ctx = tempCanvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            ctx.drawImage(canvas, 0, yOffsetCanvas, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
            const sliceData = tempCanvas.toDataURL("image/jpeg", 0.92);
            const sliceRatio = sliceH / canvas.width;
            const sliceImgH = pdfW * sliceRatio;
            if (i > 0 || !isFirstChunk) pdf.addPage();
            pdf.addImage(sliceData, "JPEG", 0, 0, pdfW, sliceImgH);
            yOffsetCanvas += sliceH;
            isFirstChunk = false;
          }
        }
      }

      const filename = `we360-reporte-${(fullName || "usuario").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      pdf.save(filename);
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 4000);
    } catch (err) {
      console.error("[ExportPreview] PDF generation failed:", err);
    } finally {
      container.style.transform = originalTransform;
      setDownloading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#F1F5F9", paddingBottom: 60 }}>
      {/* ── Action bar (NOT in PDF) ── */}
      <div
        style={{
          position: "sticky", top: 0, zIndex: 10,
          background: "white", borderBottom: "1px solid #E2E8F0",
          padding: "12px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => navigate("/mi-cuenta")}
          style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 13,
            color: "#64748B", background: "none", border: "none", cursor: "pointer",
          }}
          data-testid="export-back-btn"
        >
          <ArrowLeft size={16} />
          Volver a mi cuenta
        </button>
        <button
          onClick={handleDownload}
          disabled={downloading}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 18px", borderRadius: 10,
            background: downloaded ? "#DCFCE7" : TEAL,
            color: downloaded ? "#166534" : "white",
            border: "none", fontSize: 13, fontWeight: 700,
            cursor: downloading ? "wait" : "pointer",
            boxShadow: "0 1px 3px rgba(0, 98, 143, 0.2)",
          }}
          data-testid="export-download-pdf-btn"
        >
          {downloading
            ? <><Loader2 size={15} className="animate-spin" /> Generando PDF…</>
            : downloaded
              ? <><CheckCircle size={15} /> Descargado</>
              : <><Download size={15} /> Descargar PDF</>}
        </button>
      </div>

      {/* Contenedor adaptativo externo para evitar desbordamiento y scroll horizontal */}
      <div
        style={{
          width: "100%",
          overflowX: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start", // Evita que la altura del contenedor padre deforme o colapse al hijo
          height: scale < 1 && containerHeight > 0 ? `${containerHeight * scale}px` : "auto",
        }}
      >
        {/* ── Slides container (each slide = 1 PDF page) ── */}
        <div
          ref={containerRef}
          data-testid="export-report-canvas"
          style={{
            margin: scale < 1 ? "0 auto" : "20px auto",
            padding: "0 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            transform: `scale(${scale})`,
            transformOrigin: "top center",
            width: PAGE_WIDTH,
            flexShrink: 0,
            height: "max-content", // Fuerza al hijo a calcular su altura real basada en sus páginas internas
          }}
        >
        {/* ──────────────── SLIDE 1: COVER + VITALS ──────────────── */}
        <PdfPage padded={false}>
          {/* Hero header */}
          <div
            style={{
              background: `linear-gradient(135deg, ${TEAL} 0%, ${MAGENTA} 100%)`,
              padding: "32px 40px",
              color: "white",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24 }}>
              <div>
                <p style={{ fontSize: 11, letterSpacing: 2, opacity: 0.85, fontWeight: 600, margin: 0, textTransform: "uppercase" }}>
                  Reporte preventivo de salud
                </p>
                <h1 style={{ fontSize: 28, fontWeight: 800, margin: "6px 0 4px", letterSpacing: -0.4 }}>
                  WE360
                </h1>
                <p style={{ fontSize: 12, opacity: 0.8, margin: 0 }}>
                  Generado el {new Date().toLocaleDateString("es-PA", { day: "2-digit", month: "long", year: "numeric" })}
                </p>
              </div>
              <div style={{
                background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.28)",
                padding: "10px 14px", borderRadius: 10, textAlign: "right",
              }}>
                <p style={{ fontSize: 10, opacity: 0.8, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>
                  Avance global
                </p>
                <p style={{ fontSize: 22, fontWeight: 800, margin: 0, lineHeight: 1.1 }}>
                  {completed}/5
                </p>
                <p style={{ fontSize: 10, opacity: 0.85, margin: 0 }}>
                  formularios
                </p>
              </div>
            </div>

            <div style={{
              marginTop: 18, padding: "12px 16px",
              background: "rgba(255,255,255,0.14)", borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
            }}>
              <p style={{ fontSize: 10, opacity: 0.8, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>
                Usuario
              </p>
              <p style={{ fontSize: 18, fontWeight: 700, margin: "2px 0", letterSpacing: -0.2 }}>
                {fullName}
              </p>
              <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>
                {[
                  age != null ? `${age} años` : null,
                  profile.sexo,
                  profile.residencia,
                ].filter(Boolean).join(" · ") || "—"}
              </p>
            </div>
          </div>

          {/* Body of slide 1 */}
          <div style={{ padding: "20px 40px 28px", display: "flex", flexDirection: "column", flex: 1 }}>
            <SectionTitle accent={TEAL}>Información del usuario</SectionTitle>
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px 20px",
              padding: "14px 16px", background: "#F8FAFC", borderRadius: 10,
              border: "1px solid #E2E8F0",
            }}>
              <InfoCell label="Email" value={profile.email || user?.email} />
              <InfoCell label="WhatsApp" value={profile.whatsapp} />
              <InfoCell label="Fecha de nacimiento" value={fmtDate(profile.fechaNacimiento)} />
              <InfoCell label="Sexo" value={profile.sexo} />
              <InfoCell label="Estado civil" value={profile.estadoCivil} />
              <InfoCell label="Nivel educativo" value={profile.nivelEducativo} />
              <InfoCell label="Profesión" value={profile.profesion} />
              <InfoCell label="Ocupación" value={profile.ocupacion} />
              <InfoCell label="Residencia" value={profile.residencia} />
            </div>

            <SectionTitle accent={MAGENTA} style={{ marginTop: 22 }}>Signos vitales y antropometría</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              <VitalCard
                icon={Activity} label="IMC" value={imc} unit={imc ? "kg/m²" : ""}
                status={imcCls?.label} color={imcCls?.color || SLATE}
              />
              <VitalCard
                icon={Heart} label="Presión arterial"
                value={profile.paSistolica && profile.paDiastolica ? `${profile.paSistolica}/${profile.paDiastolica}` : null}
                unit={profile.paSistolica ? "mmHg" : ""}
                status={bpCls?.label} color={bpCls?.color || SLATE}
              />
              <VitalCard
                icon={Activity} label="Glucemia" value={profile.glucemia} unit={profile.glucemia ? "mg/dl" : ""}
                status={gluCls?.label} color={gluCls?.color || SLATE}
              />
              <VitalCard
                icon={Heart} label="Oximetría" value={profile.oximetria} unit={profile.oximetria ? "%" : ""}
                status={!isNaN(oxiVal) ? statusFromColor(oxiColor) : null} color={oxiColor}
              />
              <VitalCard icon={Activity} label="Peso" value={profile.peso} unit={profile.peso ? "kg" : ""} />
              <VitalCard icon={Activity} label="Estatura" value={profile.talla} unit={profile.talla ? "cm" : ""} />
              {profile.cintura && <VitalCard icon={Activity} label="Cintura" value={profile.cintura} unit="cm" />}
              {profile.heartRate && <VitalCard icon={Heart} label="Frecuencia cardíaca" value={profile.heartRate} unit="lpm" />}
              {profile.cardiovascularAge && <VitalCard icon={Heart} label="Edad cardiovascular" value={profile.cardiovascularAge} unit="años" />}
            </div>

            {(profile.condicionIds?.length > 0 || profile.medicamentos?.length > 0) && (
              <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {profile.condicionIds?.length > 0 && (
                  <div style={{ background: "#FEF3F2", border: "1px solid #FECACA", borderRadius: 10, padding: "10px 12px" }}>
                    <p style={{ fontSize: 9, color: "#B91C1C", textTransform: "uppercase", fontWeight: 700, margin: 0, letterSpacing: 0.6 }}>
                      Condiciones declaradas
                    </p>
                    <p style={{ fontSize: 11, color: "#991B1B", margin: "5px 0 0", lineHeight: 1.5 }}>
                      {profile.condicionIds.length} condición(es): {(profile.condicionIds || []).slice(0, 8).join(", ")}
                      {profile.condicionIds.length > 8 ? "…" : ""}
                    </p>
                  </div>
                )}
                {profile.medicamentos?.length > 0 && (
                  <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 12px" }}>
                    <p style={{ fontSize: 9, color: "#92400E", textTransform: "uppercase", fontWeight: 700, margin: 0, letterSpacing: 0.6 }}>
                      Medicamentos en uso
                    </p>
                    <p style={{ fontSize: 11, color: "#78350F", margin: "5px 0 0", lineHeight: 1.5 }}>
                      {profile.medicamentos.map((m) => m.nombre || m.name).filter(Boolean).slice(0, 8).join(", ")}
                      {profile.medicamentos.length > 8 ? "…" : ""}
                    </p>
                  </div>
                )}
              </div>
            )}

            <PageFooter pageNum={pageNumbers.cover} totalPages={totalPages} patient={fullName} />
          </div>
        </PdfPage>

        {/* ──────────────── SLIDE 2: HISTORIAL + CHECKLIST ──────────────── */}
        <PdfPage>
          <SectionTitle accent={YELLOW}>Historial de mediciones de seguimiento</SectionTitle>

          {loadingMeas ? (
            <div style={{ padding: "40px", textAlign: "center", color: SLATE_400, fontSize: 12 }}>
              Cargando mediciones…
            </div>
          ) : !hasAnyChart ? (
            <div style={{ padding: "30px 16px", textAlign: "center", color: SLATE_400, fontSize: 12, background: "#F8FAFC", borderRadius: 10, border: "1px dashed #CBD5E1" }}>
              Aún no hay mediciones de seguimiento registradas en el Wellness Hub.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Chart 1: Blood pressure */}
              {bpSeries.length > 0 && (
                <ChartCard title="Presión arterial" subtitle="mmHg">
                  <div style={{ width: 690, height: 180 }}>
                    <LineChart width={690} height={180} data={bpRows} margin={{ top: 4, right: 14, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: SLATE_400 }} stroke="#CBD5E1" />
                      <YAxis tick={{ fontSize: 9, fill: SLATE_400 }} stroke="#CBD5E1" domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E2E8F0" }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} iconType="circle" />
                      {bpSeries.map((s) => (
                        <Line
                           key={s.key} type="monotone" dataKey={s.key} stroke={s.color}
                           strokeWidth={2.5} dot={{ r: 3, fill: s.color }} activeDot={{ r: 5 }}
                           connectNulls isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </div>
                </ChartCard>
              )}

              {/* Chart 2: Other vitals */}
              {otherSeries.length > 0 && (
                <ChartCard title="Glucemia, frecuencia cardíaca y SpO₂" subtitle="varias unidades">
                  <div style={{ width: 690, height: 180 }}>
                    <LineChart width={690} height={180} data={otherRows} margin={{ top: 4, right: 14, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: SLATE_400 }} stroke="#CBD5E1" />
                      <YAxis tick={{ fontSize: 9, fill: SLATE_400 }} stroke="#CBD5E1" domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E2E8F0" }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} iconType="circle" />
                      {otherSeries.map((s) => (
                        <Line
                           key={s.key} type="monotone" dataKey={s.key} stroke={s.color}
                           strokeWidth={2.5} dot={{ r: 3, fill: s.color }} activeDot={{ r: 5 }}
                           connectNulls isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </div>
                </ChartCard>
              )}

              <p style={{ fontSize: 10, color: SLATE_400, textAlign: "center", margin: "0" }}>
                Últimas {chartRows.length} mediciones registradas
              </p>
            </div>
          )}

          <SectionTitle accent={TEAL} style={{ marginTop: 24 }}>Evaluaciones completadas</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
            <FormCheck
              label="F1 · Demográfico"
              done={hasF1}
              score={hasF1 ? `Completado` : "Pendiente"}
            />
            <FormCheck
              label="F2 · Estado físico"
              done={hasF2}
              score={hasF2 && profile.f2Results ? `Apto: ${profile.f2Results.parqApto ? "Sí" : "No"}` : "Pendiente"}
            />
            <FormCheck
              label="F3 · Actividad (IPAQ)"
              done={hasF3}
              score={hasF3 && profile.f3Results ? `${ipaqLabel(profile.f3Results.clasificacionIPAQ)}` : "Pendiente"}
            />
            <FormCheck
              label="F4 · Nutrición"
              done={hasF4}
              score={hasF4 && profile.f4Results ? `${profile.f4Results.scoreCalidadDieta}/40 · ${dietCatLabel(profile.f4Results.categoriaCalidadDieta)}` : "Pendiente"}
            />
            <FormCheck
              label="F5 · Bienestar"
              done={hasF5}
              score={hasF5 && profile.f5Results ? `${profile.f5Results.scoreGlobal}/100 · ${emotionalLabel(profile.f5Results.categoriaGlobal)}` : "Pendiente"}
            />
          </div>

          <PageFooter pageNum={pageNumbers.history} totalPages={totalPages} patient={fullName} />
        </PdfPage>

        {/* ──────────────── SLIDE 3: DETAIL PER DIMENSION ──────────────── */}
        {showDetailSlide && (
          <PdfPage>
            <SectionTitle accent={MAGENTA}>Detalle clínico por dimensión</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
              {hasF2 && (
                <DimensionCard icon={Dumbbell} title="Físico" accent={TEAL}>
                  <Row k="6MWT" v={profile.f2Results.distancia6mwt ? `${profile.f2Results.distancia6mwt} m · ${profile.f2Results.clasificacion6mwt || "—"}` : "—"} />
                  <Row k="Sentadillas" v={profile.f2Results.sentadillas ? `${profile.f2Results.sentadillas} · ${profile.f2Results.clasificacionSentadillas || "—"}` : "—"} />
                  <Row k="Flexiones" v={profile.f2Results.flexiones ? `${profile.f2Results.flexiones} · ${profile.f2Results.clasificacionFlexiones || "—"}` : "—"} />
                  <Row k="Flexibilidad" v={profile.f2Results.clasificacionFlexibilidad || "—"} />
                  <Row k="PAR-Q apto" v={profile.f2Results.parqApto ? "Sí" : "No"} />
                </DimensionCard>
              )}
              {hasF3 && (
                <DimensionCard icon={Activity} title="Actividad física (IPAQ)" accent={YELLOW}>
                  <Row k="MET total" v={`${profile.f3Results.metTotal || 0} MET-min/sem`} />
                  <Row k="Clasificación" v={ipaqLabel(profile.f3Results.clasificacionIPAQ)} />
                  <Row k="Sedentarismo" v={profile.f3Results.indiceSedentarismo ? sedLabel(profile.f3Results.indiceSedentarismo) : "—"} />
                </DimensionCard>
              )}
              {hasF4 && (
                <DimensionCard icon={Apple} title="Nutrición (FFQ)" accent={GREEN}>
                  <Row k="Calidad de dieta" v={`${profile.f4Results.scoreCalidadDieta}/40 · ${dietCatLabel(profile.f4Results.categoriaCalidadDieta)}`} />
                  <Row k="Conductas" v={`${profile.f4Results.scoreConductas}/15 · ${behaviorCatLabel(profile.f4Results.categoriaConductas)}`} />
                  <Row k="Hidratación" v={hydrationLabel(profile.f4Results.categoriaHidratacion)} />
                </DimensionCard>
              )}
              {hasF5 && (
                <DimensionCard icon={Brain} title="Bienestar emocional" accent={MAGENTA}>
                  <Row k="Score global" v={`${profile.f5Results.scoreGlobal}/100 · ${emotionalLabel(profile.f5Results.categoriaGlobal)}`} />
                  {profile.f5Results.ansiedad && <Row k="Ansiedad" v={`${profile.f5Results.ansiedad.score}/15 · ${anxietyLabel(profile.f5Results.ansiedad.categoria)}`} />}
                  {profile.f5Results.estres && <Row k="Estrés" v={`${profile.f5Results.estres.score}/20 · ${stressLabel(profile.f5Results.estres.categoria)}`} />}
                  {profile.f5Results.bienestar && <Row k="Bienestar" v={`${profile.f5Results.bienestar.score}/25 · ${wellbeingLabel(profile.f5Results.bienestar.categoria)}`} />}
                </DimensionCard>
              )}
            </div>

            <PageFooter pageNum={pageNumbers.detail} totalPages={totalPages} patient={fullName} />
          </PdfPage>
        )}

        {/* ──────────────── SLIDE 4: AI SUMMARY + PLAN ──────────────── */}
        {hasAI && (
          <PdfPage>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Sparkles size={16} style={{ color: MAGENTA }} />
              <h2 style={{ fontSize: 14, fontWeight: 800, color: SLATE, margin: 0, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Recomendaciones generadas por IA
              </h2>
            </div>

            {summary?.resumen_ejecutivo && (
              <div style={{
                background: `linear-gradient(135deg, ${TEAL_LIGHT} 0%, ${MAGENTA_LIGHT} 100%)`,
                border: `1px solid ${MAGENTA}33`,
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 12,
              }}>
                <p style={{ fontSize: 9, color: MAGENTA, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, margin: 0, marginBottom: 4 }}>
                  Resumen ejecutivo
                </p>
                <p style={{ fontSize: 12, color: SLATE, lineHeight: 1.55, margin: 0 }}>
                  {summary.resumen_ejecutivo}
                </p>
              </div>
            )}

            {summary?.alertas_y_riesgos && (summary.alertas_y_riesgos.nivel_alerta || summary.alertas_y_riesgos.motivo || summary.alertas_y_riesgos.accion_requerida) && (
              <div style={{
                background: "white",
                borderLeft: `4px solid ${alertColor(summary.alertas_y_riesgos.nivel_alerta)}`,
                border: "1px solid #E2E8F0",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 12,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: summary.alertas_y_riesgos.motivo ? 8 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <AlertTriangle size={13} style={{ color: alertColor(summary.alertas_y_riesgos.nivel_alerta) }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: SLATE }}>Alertas y riesgos</span>
                  </div>
                  {summary.alertas_y_riesgos.nivel_alerta && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: `${alertColor(summary.alertas_y_riesgos.nivel_alerta)}1a`, color: alertColor(summary.alertas_y_riesgos.nivel_alerta), textTransform: "uppercase", letterSpacing: 0.4 }}>
                      {summary.alertas_y_riesgos.nivel_alerta}
                    </span>
                  )}
                </div>
                {summary.alertas_y_riesgos.motivo && (
                  <p style={{ fontSize: 11, color: SLATE, lineHeight: 1.5, margin: 0, marginBottom: summary.alertas_y_riesgos.accion_requerida ? 8 : 0 }}>
                    {summary.alertas_y_riesgos.motivo}
                  </p>
                )}
                {summary.alertas_y_riesgos.accion_requerida && (
                  <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 10px" }}>
                    <p style={{ fontSize: 10.5, color: "#78350F", margin: 0, lineHeight: 1.5 }}>
                      <strong>Acción requerida:</strong> {summary.alertas_y_riesgos.accion_requerida}
                    </p>
                  </div>
                )}
              </div>
            )}

            {summary?.plan_maestro_hoy?.prioridad_numero_1 && (
              <div style={{ background: "#F0F9FF", border: `1px solid ${TEAL}33`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
                <p style={{ fontSize: 9, color: TEAL, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, margin: 0, marginBottom: 4 }}>
                  Prioridad #1 de hoy
                </p>
                <p style={{ fontSize: 12, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                  {summary.plan_maestro_hoy.prioridad_numero_1}
                </p>
              </div>
            )}

            {Array.isArray(summary?.plan_maestro_hoy?.micro_intervenciones) && summary.plan_maestro_hoy.micro_intervenciones.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <p style={{ fontSize: 9, color: SLATE_500, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, margin: 0, marginBottom: 6 }}>
                  Micro-intervenciones recomendadas
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, color: SLATE, fontSize: 11, lineHeight: 1.55 }}>
                  {summary.plan_maestro_hoy.micro_intervenciones.map((m, i) => (
                    <li key={i} style={{ marginBottom: 3 }}>{typeof m === "string" ? m : (m.descripcion || m.titulo || JSON.stringify(m))}</li>
                  ))}
                </ul>
              </div>
            )}

            {summary?.plan_maestro_hoy?.coherencia_entre_pilares && (
              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
                <p style={{ fontSize: 9, color: "#92400E", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, margin: 0, marginBottom: 4 }}>
                  Coherencia entre pilares
                </p>
                <p style={{ fontSize: 11, color: "#78350F", margin: 0, lineHeight: 1.5 }}>
                  {summary.plan_maestro_hoy.coherencia_entre_pilares}
                </p>
              </div>
            )}

            {(summary?.motivacion_y_logros?.reto_general_semanal || summary?.motivacion_y_logros?.mensaje_coaching) && (
              <div style={{
                background: `linear-gradient(135deg, ${TEAL} 0%, #0080A0 50%, #2A9D8F 100%)`,
                borderRadius: 10,
                padding: "14px 16px",
                marginTop: "auto",
                color: "white",
              }}>
                {summary.motivacion_y_logros.reto_general_semanal && (
                  <>
                    <p style={{ fontSize: 9, color: "rgba(255,255,255,0.8)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, margin: 0, marginBottom: 4 }}>
                      Reto de la semana
                    </p>
                    <p style={{ fontSize: 12, fontWeight: 700, margin: 0, marginBottom: 8, lineHeight: 1.4 }}>
                      {summary.motivacion_y_logros.reto_general_semanal}
                    </p>
                  </>
                )}
                {summary.motivacion_y_logros.mensaje_coaching && (
                  <p style={{ fontSize: 11, opacity: 0.92, margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>
                    "{summary.motivacion_y_logros.mensaje_coaching}"
                  </p>
                )}
              </div>
            )}

            {capsulaNeuro && capsulaNeuro.length <= 350 && (
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "10px 12px", marginTop: 10 }}>
                <p style={{ fontSize: 9, color: MAGENTA, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, margin: 0, marginBottom: 4 }}>
                  Cápsula de neurociencia
                </p>
                <p style={{ fontSize: 11, color: SLATE, margin: 0, lineHeight: 1.55 }}>
                  {capsulaNeuro}
                </p>
              </div>
            )}

            <p style={{ fontSize: 9, color: SLATE_400, marginTop: 10, marginBottom: 0, textAlign: "right" }}>
              Generado el {fmtDate(profile.recommendations?.generatedAt)} · Plan completo en la app WE360
            </p>

            <PageFooter pageNum={pageNumbers.aiSummary} totalPages={totalPages} patient={fullName} />
          </PdfPage>
        )}

        {/* ──────────────── SLIDE 5: PILAR FÍSICO ──────────────── */}
        {showPhysical && (
          <PdfPage>
            <PillarHeader icon={Dumbbell} title="Pilar Físico" accent={YELLOW} subtitle={physical.classification?.nivel_riesgo ? `Riesgo ${physical.classification.nivel_riesgo}` : "Tu plan físico"} />

            {physical.classification?.condicion_fisica && (
              <PillarBlock label="Diagnóstico">
                <p style={pillarText()}>{physical.classification.condicion_fisica}</p>
              </PillarBlock>
            )}

            {physical.planSummary && (
              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <p style={{ fontSize: 10.5, color: "#78350F", margin: 0, lineHeight: 1.5 }}>
                  <strong>Plan:</strong> {physical.planSummary}
                </p>
              </div>
            )}

            {physical.fases && Object.keys(physical.fases).length > 0 && (
              <PillarBlock label="Fases del programa">
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { k: "adaptacion", label: "Adaptación" },
                    { k: "progresion", label: "Progresión" },
                    { k: "consolidacion", label: "Consolidación" },
                  ].filter((f) => physical.fases[f.k]).map((f) => (
                    <PhaseCard key={f.k} label={f.label} fase={physical.fases[f.k]} />
                  ))}
                </div>
              </PillarBlock>
            )}

            {(physical.verticales?.fuerza || physical.verticales?.aerobico || physical.verticales?.flexibilidad) && (
              <PillarBlock label="Tipos de ejercicio sugeridos">
                {physical.verticales.fuerza && <VerticalRow label="Fuerza" text={physical.verticales.fuerza} color={YELLOW} />}
                {physical.verticales.aerobico && <VerticalRow label="Aeróbico" text={physical.verticales.aerobico} color={YELLOW} />}
                {physical.verticales.flexibilidad && <VerticalRow label="Flexibilidad" text={physical.verticales.flexibilidad} color={YELLOW} />}
              </PillarBlock>
            )}

            {(physical.gamificacion?.reto_semanal || physical.gamificacion?.mensaje_motivacional) && (
              <div style={{ background: "#FFF3DC", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 12px", marginTop: "auto" }}>
                {physical.gamificacion.reto_semanal && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <Flame size={11} color={YELLOW} />
                      <span style={{ fontSize: 9, fontWeight: 700, color: YELLOW, textTransform: "uppercase", letterSpacing: 0.4 }}>Reto semanal · Físico</span>
                    </div>
                    <p style={{ fontSize: 11, color: SLATE, fontWeight: 600, margin: 0, marginBottom: physical.gamificacion.mensaje_motivacional ? 6 : 0, lineHeight: 1.45 }}>
                      {physical.gamificacion.reto_semanal}
                    </p>
                  </>
                )}
                {physical.gamificacion.mensaje_motivacional && (
                  <p style={{ fontSize: 10.5, color: SLATE_500, fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>
                    {physical.gamificacion.mensaje_motivacional}
                  </p>
                )}
              </div>
            )}

            <PageFooter pageNum={pageNumbers.physical} totalPages={totalPages} patient={fullName} />
          </PdfPage>
        )}

        {/* ──────────────── SLIDE 6: PILAR NUTRICIONAL ──────────────── */}
        {showNutrition && (
          <PdfPage>
            <PillarHeader icon={Apple} title="Pilar Nutricional" accent={GREEN} subtitle={nutrition.evaluacion_funcional?.clasificacion_dieta || "Tu plan alimentario"} />

            {nutrition.evaluacion_funcional?.diagnostico && (
              <PillarBlock label="Diagnóstico">
                <p style={pillarText()}>{nutrition.evaluacion_funcional.diagnostico}</p>
              </PillarBlock>
            )}

            {Array.isArray(nutrition.evaluacion_funcional?.puntos_ciegos) && nutrition.evaluacion_funcional.puntos_ciegos.length > 0 && (
              <PillarBlock label="Puntos ciegos" labelColor={RED}>
                {nutrition.evaluacion_funcional.puntos_ciegos.map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 4 }}>
                    <AlertTriangle size={10} color={RED} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={pillarText()}>{p}</span>
                  </div>
                ))}
              </PillarBlock>
            )}

            {nutrition.programa_progresivo?.objetivos_fase && (
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <p style={{ fontSize: 10.5, color: "#166534", margin: 0, lineHeight: 1.5 }}>
                  {nutrition.programa_progresivo.fase_actual && <strong>{nutrition.programa_progresivo.fase_actual}: </strong>}
                  {nutrition.programa_progresivo.objetivos_fase}
                </p>
              </div>
            )}

            {Array.isArray(nutrition.programa_progresivo?.intervenciones_clave) && nutrition.programa_progresivo.intervenciones_clave.length > 0 && (
              <PillarBlock label="Intervenciones clave" labelColor={GREEN}>
                {nutrition.programa_progresivo.intervenciones_clave.map((it, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 4 }}>
                    <CheckCircle size={10} color={GREEN} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={pillarText()}>{it}</span>
                  </div>
                ))}
              </PillarBlock>
            )}

            {(nutrition.plan_diario?.desayuno || nutrition.plan_diario?.almuerzo || nutrition.plan_diario?.cena) && (
              <PillarBlock label="Plan diario sugerido">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {nutrition.plan_diario.desayuno && <MealCard label="Desayuno" meal={nutrition.plan_diario.desayuno} />}
                  {nutrition.plan_diario.almuerzo && <MealCard label="Almuerzo" meal={nutrition.plan_diario.almuerzo} />}
                  {nutrition.plan_diario.cena && <MealCard label="Cena" meal={nutrition.plan_diario.cena} />}
                  {Array.isArray(nutrition.plan_diario.power_snacks) && nutrition.plan_diario.power_snacks.length > 0 && (
                    <div style={{ background: "#F8FAFC", border: "1px solid #E2D9CB", borderRadius: 8, padding: "8px 10px" }}>
                      <p style={{ fontSize: 9, color: GREEN, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, margin: 0, marginBottom: 4 }}>Power snacks</p>
                      {nutrition.plan_diario.power_snacks.map((s, i) => (
                        <div key={i} style={{ marginBottom: 3 }}>
                          <p style={{ fontSize: 10.5, fontWeight: 600, color: SLATE, margin: 0 }}>• {s.alimentos}</p>
                          {s.por_que_funciona && <p style={{ fontSize: 9.5, color: SLATE_500, margin: 0, marginLeft: 8, lineHeight: 1.4 }}>{s.por_que_funciona}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </PillarBlock>
            )}

            {(nutrition.estrategia_adherencia?.reto_semanal || (Array.isArray(nutrition.estrategia_adherencia?.metas_diarias) && nutrition.estrategia_adherencia.metas_diarias.length > 0) || nutrition.estrategia_adherencia?.mensaje_refuerzo) && (
              <div style={{ background: "#E2F0E8", border: `1px solid ${GREEN}33`, borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
                {nutrition.estrategia_adherencia.reto_semanal && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <Flame size={11} color={GREEN} />
                      <span style={{ fontSize: 9, fontWeight: 700, color: GREEN, textTransform: "uppercase", letterSpacing: 0.4 }}>Reto semanal · Nutrición</span>
                    </div>
                    <p style={{ fontSize: 11, color: SLATE, fontWeight: 600, margin: 0, marginBottom: 6, lineHeight: 1.45 }}>
                      {nutrition.estrategia_adherencia.reto_semanal}
                    </p>
                  </>
                )}
                {Array.isArray(nutrition.estrategia_adherencia.metas_diarias) && nutrition.estrategia_adherencia.metas_diarias.length > 0 && (
                  <div style={{ marginBottom: nutrition.estrategia_adherencia.mensaje_refuerzo ? 6 : 0 }}>
                    <p style={{ fontSize: 9, color: GREEN, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, margin: 0, marginBottom: 3 }}>Metas diarias</p>
                    {nutrition.estrategia_adherencia.metas_diarias.map((m, i) => (
                      <p key={i} style={{ fontSize: 10.5, color: SLATE, margin: 0, lineHeight: 1.45 }}>• {m}</p>
                    ))}
                  </div>
                )}
                {nutrition.estrategia_adherencia.mensaje_refuerzo && (
                  <p style={{ fontSize: 10.5, color: SLATE_500, fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>
                    {nutrition.estrategia_adherencia.mensaje_refuerzo}
                  </p>
                )}
              </div>
            )}

            {nutrition.educacion_microlearning?.capsula_conocimiento && (
              <div style={{ background: "#F0F9FF", border: `1px solid ${TEAL}33`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <BookOpen size={11} color={TEAL} />
                  <span style={{ fontSize: 9, fontWeight: 700, color: TEAL, textTransform: "uppercase", letterSpacing: 0.4 }}>Cápsula de conocimiento</span>
                </div>
                <p style={{ fontSize: 10.5, color: SLATE, lineHeight: 1.55, margin: 0 }}>
                  {nutrition.educacion_microlearning.capsula_conocimiento}
                </p>
              </div>
            )}

            <PageFooter pageNum={pageNumbers.nutrition} totalPages={totalPages} patient={fullName} />
          </PdfPage>
        )}

        {/* ──────────────── SLIDE 7: PILAR EMOCIONAL ──────────────── */}
        {showEmotional && (
          <PdfPage>
            <PillarHeader icon={Brain} title="Pilar Emocional" accent={MAGENTA} subtitle={emotional.evaluacion_sna?.estado_actual ? "Bienestar mental" : "Tu plan de bienestar mental"} />

            {emotional.evaluacion_sna?.estado_actual && (
              <PillarBlock label="Estado actual">
                <p style={pillarText()}>{emotional.evaluacion_sna.estado_actual}</p>
              </PillarBlock>
            )}

            {emotional.evaluacion_sna?.analisis_fisiologico && (
              <div style={{ background: TEAL_LIGHT, border: `1px solid ${TEAL}33`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <p style={{ fontSize: 10.5, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                  {emotional.evaluacion_sna.analisis_fisiologico}
                </p>
              </div>
            )}

            {Array.isArray(emotional.evaluacion_sna?.alertas_bienestar) && emotional.evaluacion_sna.alertas_bienestar.length > 0 && (
              <PillarBlock label="Alertas de bienestar" labelColor={RED}>
                {emotional.evaluacion_sna.alertas_bienestar.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 4 }}>
                    <AlertTriangle size={10} color={RED} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={pillarText()}>{a}</span>
                  </div>
                ))}
              </PillarBlock>
            )}

            {emotional.plan_progresivo?.tecnica_rescate?.nombre && (
              <div style={{ background: "#F4E8F1", border: `1px solid ${MAGENTA}33`, borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <Lightbulb size={11} color={MAGENTA} />
                  <span style={{ fontSize: 9, fontWeight: 700, color: MAGENTA, textTransform: "uppercase", letterSpacing: 0.4 }}>Técnica de rescate</span>
                </div>
                <p style={{ fontSize: 11, fontWeight: 600, color: SLATE, margin: 0, marginBottom: 4 }}>
                  {emotional.plan_progresivo.tecnica_rescate.nombre}
                </p>
                {emotional.plan_progresivo.tecnica_rescate.instrucciones_paso_a_paso && (
                  <p style={{ fontSize: 10.5, color: SLATE_500, lineHeight: 1.55, margin: 0, whiteSpace: "pre-wrap" }}>
                    {emotional.plan_progresivo.tecnica_rescate.instrucciones_paso_a_paso}
                  </p>
                )}
              </div>
            )}

            {(() => {
              const bio = emotional.intervenciones_biohacking || {};
              const manana = bio["mañana_activacion"] || bio.manana_activacion;
              const noche = bio["noche_rituo_desactivacion"] || bio["noche_ritual_desactivacion"] || bio.noche_ritual_desactivacion;
              if (!manana && !bio.trabajo_coherencia && !noche) return null;
              return (
                <PillarBlock label="Intervenciones del día">
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {manana && <BioRow icon={Sun} color={YELLOW} label="Mañana · activación" text={manana} />}
                    {bio.trabajo_coherencia && <BioRow icon={Activity} color={TEAL} label="Trabajo · coherencia" text={bio.trabajo_coherencia} />}
                    {noche && <BioRow icon={Moon} color={MAGENTA} label="Noche · desactivación" text={noche} />}
                  </div>
                </PillarBlock>
              );
            })()}

            {(emotional.entorno_circadiano?.sugerencia_luz || emotional.entorno_circadiano?.control_ambiental) && (
              <PillarBlock label="Entorno circadiano">
                {emotional.entorno_circadiano.sugerencia_luz && (
                  <p style={{ ...pillarText(), marginBottom: 4 }}>
                    <strong style={{ color: MAGENTA }}>Luz:</strong> {emotional.entorno_circadiano.sugerencia_luz}
                  </p>
                )}
                {emotional.entorno_circadiano.control_ambiental && (
                  <p style={pillarText()}>
                    <strong style={{ color: MAGENTA }}>Ambiente:</strong> {emotional.entorno_circadiano.control_ambiental}
                  </p>
                )}
              </PillarBlock>
            )}

            <PageFooter pageNum={pageNumbers.emotional} totalPages={totalPages} patient={fullName} />
          </PdfPage>
        )}

        {/* ──────────────── SLIDE 8: AI CAPSULA EDUCATIVA (sólo si > 350 chars) ──────────────── */}
        {showCapsulaSlide && (
          <PdfPage>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Brain size={16} style={{ color: MAGENTA }} />
              <h2 style={{ fontSize: 14, fontWeight: 800, color: SLATE, margin: 0, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Cápsula educativa · neurociencia
              </h2>
            </div>

            <div style={{
              background: `linear-gradient(135deg, ${MAGENTA_LIGHT} 0%, ${TEAL_LIGHT} 100%)`,
              border: `1px solid ${MAGENTA}33`,
              borderRadius: 12,
              padding: "20px 24px",
              flex: 1,
            }}>
              <p style={{ fontSize: 10, color: MAGENTA, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, margin: 0, marginBottom: 10 }}>
                Microlearning personalizado
              </p>
              <p style={{ fontSize: 13, color: SLATE, lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>
                {capsulaNeuro}
              </p>
            </div>

            <p style={{ fontSize: 10, color: SLATE_400, marginTop: 14, marginBottom: 0, textAlign: "center" }}>
              Plan IA completo (físico + nutricional + emocional) disponible en la app WE360.
            </p>

            <PageFooter pageNum={pageNumbers.aiCapsule} totalPages={totalPages} patient={fullName} />
          </PdfPage>
        )}
        </div>
      </div>
    </div>
  );
}

// ── helpers / small components ─────────────────────────────────────────────

function alertColor(level) {
  const k = (level || "").toLowerCase();
  if (k.includes("rojo") || k.includes("alto") || k.includes("crítico")) return RED;
  if (k.includes("amarillo") || k.includes("medio") || k.includes("moderado")) return YELLOW;
  if (k.includes("verde") || k.includes("bajo")) return GREEN;
  return MAGENTA;
}

function pillarText() {
  return { fontSize: 10.5, color: SLATE, lineHeight: 1.55, margin: 0 };
}

function PillarHeader({ icon: Icon, title, accent, subtitle }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      paddingBottom: 12, marginBottom: 14,
      borderBottom: `2px solid ${accent}`,
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: `${accent}1a`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={18} style={{ color: accent }} />
      </div>
      <div>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: SLATE, margin: 0, letterSpacing: -0.2 }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{ fontSize: 10, color: SLATE_500, margin: 0, marginTop: 2 }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

function PillarBlock({ label, labelColor = SLATE_500, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <p style={{ fontSize: 9, color: labelColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, margin: 0, marginBottom: 6 }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function PhaseCard({ label, fase }) {
  return (
    <div style={{ background: "#FCFAEE", border: "1px solid #E2D9CB", borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: SLATE }}>{label}</span>
        {fase.semanas && (
          <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 5, background: "#FFF3DC", color: YELLOW }}>
            Semanas {fase.semanas}
          </span>
        )}
      </div>
      {fase.objetivo && (
        <p style={{ fontSize: 10.5, color: SLATE_500, margin: 0, marginBottom: fase.ejercicios?.length ? 6 : 0, lineHeight: 1.5 }}>
          {fase.objetivo}
        </p>
      )}
      {Array.isArray(fase.ejercicios) && fase.ejercicios.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {fase.ejercicios.map((ej, i) => (
            <div key={i} style={{ background: "white", border: "1px solid #E2D9CB", borderRadius: 6, padding: "6px 8px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ fontSize: 9.5, fontWeight: 700, color: YELLOW }}>{ej.tipo}</span>
                {ej.frecuencia && <span style={{ fontSize: 9, color: SLATE_500 }}>{ej.frecuencia}</span>}
              </div>
              {ej.actividad && (
                <p style={{ fontSize: 10, color: SLATE, margin: 0, lineHeight: 1.4 }}>{ej.actividad}</p>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 3 }}>
                {ej.duracion && <Pill label="Duración" value={ej.duracion} />}
                {ej.series && <Pill label="Series" value={ej.series} />}
                {ej.repeticiones && <Pill label="Reps" value={ej.repeticiones} />}
                {ej.intensidad && <Pill label="Intensidad" value={ej.intensidad} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <span style={{ fontSize: 8.5, padding: "2px 5px", borderRadius: 4, background: "#E3F0F5", color: TEAL }}>
      <strong>{label}:</strong> {value}
    </span>
  );
}

function VerticalRow({ label, text, color }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color, marginRight: 4 }}>{label}:</span>
      <span style={{ fontSize: 10.5, color: SLATE }}>{text}</span>
    </div>
  );
}

function MealCard({ label, meal }) {
  return (
    <div style={{ background: "white", border: "1px solid #E2D9CB", borderRadius: 8, padding: "8px 10px" }}>
      <p style={{ fontSize: 9, color: GREEN, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, margin: 0, marginBottom: 3 }}>
        {label}
      </p>
      {meal.alimentos && (
        <p style={{ fontSize: 10.5, fontWeight: 600, color: SLATE, margin: 0, marginBottom: 3, lineHeight: 1.45 }}>
          {meal.alimentos}
        </p>
      )}
      {meal.preparacion && (
        <p style={{ fontSize: 9.5, color: SLATE_500, margin: 0, marginBottom: 2, lineHeight: 1.4 }}>
          <strong>Preparación:</strong> {meal.preparacion}
        </p>
      )}
      {meal.por_que_funciona && (
        <p style={{ fontSize: 9.5, color: SLATE_500, fontStyle: "italic", margin: 0, lineHeight: 1.4 }}>
          {meal.por_que_funciona}
        </p>
      )}
    </div>
  );
}

function BioRow({ icon: Icon, color, label, text }) {
  return (
    <div style={{ background: "#FCFAEE", border: "1px solid #E2D9CB", borderRadius: 8, padding: "8px 10px", display: "flex", gap: 8 }}>
      <div style={{
        width: 26, height: 26, borderRadius: 6,
        background: `${color}1a`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Icon size={13} color={color} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 9.5, fontWeight: 700, color, margin: 0, marginBottom: 2 }}>{label}</p>
        <p style={{ fontSize: 10.5, color: SLATE, margin: 0, lineHeight: 1.45 }}>{text}</p>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 10, padding: "12px 12px 4px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, paddingLeft: 6 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: SLATE, margin: 0 }}>{title}</p>
        {subtitle && <p style={{ fontSize: 9, color: SLATE_400, margin: 0 }}>· {subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function DimensionCard({ icon: Icon, title, accent, children }) {
  return (
    <div style={{
      background: "white", border: "1px solid #E2E8F0", borderTop: `3px solid ${accent}`,
      borderRadius: 10, padding: "12px 14px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon size={14} style={{ color: accent }} />
        <p style={{ fontSize: 11, fontWeight: 700, color: SLATE, margin: 0, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {title}
        </p>
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11 }}>
      <span style={{ color: SLATE_400 }}>{k}</span>
      <span style={{ color: SLATE, fontWeight: 600, textAlign: "right" }}>{v}</span>
    </div>
  );
}
